import { useState } from 'react'

import Vehicle from '../interfaces/Vehicle'
import MeliInputVehicle from '../interfaces/MeliInputVehicle'
import { BranchDto } from '../interfaces/Branch'
import UserData from '../interfaces/UserData'
import { validateFields, Rule } from '../utils/validateForm'
import { extractYouTubeId } from '../utils/youtubeUtils'

// Core interfaces for shared logic
interface MeliPublishingConfig {
  vehicle: Vehicle
  formData: any
  userData: UserData
  branch: BranchDto
  publicImages: string[]
  characteristics: string[]
  region: any
}

interface MeliValidationResult {
  isValid: boolean
  errors: Record<string, string>
  warnings?: string[]
}

// Shared validation constants (consistent with individual publishing)
const MELI_VALIDATION_RULES = {
  REQUIRED_FIELDS: ['type', 'body', 'brand', 'model', 'fuel', 'gearbox', 'color', 'title', 'street', 'comments'],
  DESCRIPTION_MIN_LENGTH: 55, // Updated to match individual publishing (Meli.tsx)
  DESCRIPTION_RECOMMENDED_LENGTH: 55,
}

// Helper function to determine default door count based on body type (consistent across all publishing methods)
const getDefaultDoorCount = (bodyType: string): number => {
  const bodyLower = bodyType.toLowerCase()

  // 2-door vehicles
  if (
    bodyLower.includes('coupé') ||
    bodyLower.includes('coupe') ||
    bodyLower.includes('convertible') ||
    bodyLower.includes('roadster')
  ) {
    return 2
  }

  // 5-door vehicles
  if (
    bodyLower.includes('hatchback') ||
    bodyLower.includes('station') ||
    bodyLower.includes('suv') ||
    bodyLower.includes('crossover') ||
    bodyLower.includes('van') ||
    bodyLower.includes('minivan')
  ) {
    return 5
  }

  // Default for sedans and most other cars
  return 4
}

const useMeliPublishingCore = () => {
  const [errors, setErrors] = useState<Record<string, string>>({})

  // CRITICAL: This is the standardized payload construction that all implementations should use
  // Based on Meli.tsx pattern (most reliable) with PublicationFormManager.tsx field mapping
  const buildMeliPayload = (config: MeliPublishingConfig): MeliInputVehicle => {
    const { vehicle, formData, userData, branch, publicImages, characteristics, region } = config

    // DEBUG: Add detailed logging to diagnose field mapping issues
    console.log('🔍 MeliPublishingCore - buildMeliPayload called with:')
    console.log('Vehicle data:', {
      color: vehicle.color,
      fuel: vehicle.fuel,
      fuel_format: vehicle.fuel_format,
      body: vehicle.body,
      body_format: vehicle.body_format,
      gearbox: vehicle.gearbox,
      gearbox_format: vehicle.gearbox_format,
    })
    console.log('Form data:', {
      color: formData.color,
      fuel: formData.fuel,
      body: formData.body,
      gearbox: formData.gearbox,
    })

    // Use the EXACT same pattern as Meli.tsx (simple and reliable)
    const basePayload: MeliInputVehicle = {
      ...vehicle, // Spread entire vehicle first (preserves all vehicle data)
      ...formData, // Override with form data (user selections take precedence)

      // Guaranteed overrides (same across all implementations)
      organization: userData.organization,
      branch: { ...branch, meliRegionId: region.meliRegionId },
      pictures: publicImages,
      characteristics,
      identifier: vehicle.external_meli?.identifier || '',

      // Field-specific handling (based on PublicationFormManager.tsx explicit mapping)
      _id: vehicle._id,
      price: vehicle.price, // Always use vehicle.price directly (not from formData)
      title: formData.title || makeTitle(vehicle),
      // CRITICAL: Use promo.message as fallback, NOT vehicle.comments (internal field)
      comments: formData.comments || vehicle.promo?.message || ' ',

      // Vehicle-specific fields with form data fallbacks
      type: formData.type || convertVehicleTypeToMeli(vehicle.type),
      brand: formData.brand || vehicle.brand,
      model: formData.model || vehicle.model,
      version: formData.version || vehicle.version,
      year: vehicle.year,
      plate: vehicle.plate,
      odometer: vehicle.odometer,
      chassisId: vehicle.chassisId || '',
      engineId: vehicle.engineId,

      // CRITICAL FIX: Use direct formData values with robust fallbacks
      // This fixes the "Kulla" color issue and provides sensible defaults for incomplete data
      color: formData.color || vehicle.color || 'Blanco', // Default to white if no color specified
      body: formData.body || vehicle.body_format || vehicle.body || 'Sedán', // Default to sedan if no body specified
      fuel: formData.fuel || vehicle.fuel_format || vehicle.fuel || 'Bencina', // Default to gasoline if no fuel specified
      gearbox: formData.gearbox || vehicle.gearbox_format || vehicle.gearbox || 'Manual', // Default to manual if no gearbox specified

      // Smart door count logic (consistent with validation requirements)
      doors: (() => {
        // For cars (automobiles), use smart logic; for motorcycles/boats, use 0
        const vehicleType = convertVehicleTypeToMeli(vehicle.type)

        if (vehicleType === 'MLC1744') {
          // Only for automobiles/cars
          return (
            formData.doors ||
            vehicle.external?.meli?.attributes?.doors ||
            getDefaultDoorCount(formData.body || vehicle.body_format || vehicle.body || 'Sedán')
          )
        }

        return 0 // Motorcycles, boats, etc. don't have doors
      })(),

      // Vehicle metadata (from PublicationFormManager.tsx)
      appraisal: vehicle.appraisal,
      documents: vehicle.documents,
      expenses: vehicle.expenses,
      incomes: vehicle.incomes,
      keys: vehicle.keys,
      maintenance: vehicle.maintenance,
      owners: vehicle.owners,
      state: vehicle.state,
      videoUrl: vehicle.videoUrl,
      videoId: vehicle.videoUrl ? extractYouTubeId(vehicle.videoUrl) : undefined,
    }

    // DEBUG: Log the final calculated values for the problematic fields
    console.log('🎯 Final payload field values:')
    console.log('Final color:', basePayload.color)
    console.log('Final fuel:', basePayload.fuel)
    console.log('Final body:', basePayload.body)
    console.log('Final gearbox:', basePayload.gearbox)
    console.log('Final pictures:', basePayload.pictures?.length || 0, 'images')
    console.log('Final videoUrl:', basePayload.videoUrl || 'NONE')
    console.log('Final videoId:', basePayload.videoId || 'NONE')
    console.log('Full payload:', basePayload)

    return basePayload
  }

  // Standardized validation (extracted from all implementations)
  const validateMeliPublishing = (config: MeliPublishingConfig): MeliValidationResult => {
    const { formData, vehicle } = config

    // Use exact same field validation as all current implementations
    const fieldsToValidate = MELI_VALIDATION_RULES.REQUIRED_FIELDS.map((field) => ({
      name: field,
      value: formData[field] || '',
      rule: Rule.REQUIRED,
    }))

    // Conditional validation - doors for cars (same logic across implementations)
    if (formData.type === 'MLC1744') {
      fieldsToValidate.push({
        name: 'doors',
        value: formData.doors?.toString() || '0',
        rule: Rule.GREATER_ZERO,
      })
    }

    const validationErrors = validateFields(fieldsToValidate)

    // Description validation
    const description = formData.comments || vehicle.comments || ''
    const warnings: string[] = []

    if (description.length < MELI_VALIDATION_RULES.DESCRIPTION_MIN_LENGTH) {
      validationErrors.comments = `Descripción debe tener al menos ${MELI_VALIDATION_RULES.DESCRIPTION_MIN_LENGTH} caracteres`
    } else if (description.length < MELI_VALIDATION_RULES.DESCRIPTION_RECOMMENDED_LENGTH) {
      warnings.push('Descripción corta, considera agregar más detalles')
    }

    return {
      isValid: Object.keys(validationErrors).length === 0,
      errors: validationErrors,
      warnings: warnings.length > 0 ? warnings : undefined,
    }
  }

  // Helper function for title generation (standardized)
  const makeTitle = (vehicle: Vehicle): string => {
    return `${vehicle.year} ${vehicle.brand} ${vehicle.model} ${vehicle.version || ''}`.trim()
  }

  // Helper function for vehicle type conversion (standardized)
  const convertVehicleTypeToMeli = (type: string): string => {
    if (!type || type === 'undefined' || type.trim() === '') {
      return 'MLC1744' // Default to Autos y camionetas
    }

    switch (type) {
      case 'TYPES.VEHICLE.AUTOMOBILE':
        return 'MLC1744' // Autos y camionetas
      case 'TYPES.VEHICLE.MOTORCYCLE':
        return 'MLC1763' // Motos
      case 'TYPES.VEHICLE.NAUTIC':
        return 'MLC1785' // Náuticos
      default:
        return 'MLC1744' // Default to automobiles
    }
  }

  // Street address helper (standardized from usePublishQueue.tsx)
  const buildStreetAddress = (branch: BranchDto): string => {
    const firstToken = (branch.address || '').split(',')[0].trim()
    let street = `${firstToken} ${branch.streetNumber || ''}`

    if (branch.addressComponents) {
      street = `${street} - ${branch.addressComponents}`
    }

    return street
  }

  // Helper function for consistent description length (used by bulk publishing)
  const ensureDescriptionLength = (description: string): string => {
    return description.length >= MELI_VALIDATION_RULES.DESCRIPTION_MIN_LENGTH
      ? description
      : 'Vehículo en excelente estado, listo para ser entregado. Cuenta con todas las características necesarias para brindar una experiencia de conducción segura y confiable.'
  }

  return {
    buildMeliPayload,
    validateMeliPublishing,
    makeTitle,
    convertVehicleTypeToMeli,
    buildStreetAddress,
    ensureDescriptionLength,
    getDefaultDoorCount,
    errors,
    setErrors,
  }
}

export default useMeliPublishingCore
